import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useQuery, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import Layout from '../../components/Layout';
import { useAutosave } from '../../hooks/useAutosave';
import NoteSidebar from './components/NoteSidebar';
import NoteEditor from './components/NoteEditor';
import { NoteEmptyState } from './components/NoteEmptyState';
import { DeleteNoteDialog } from './components/DeleteNoteDialog';
import { GET_PEOPLE } from '../People/queries';
import { getLinkedPeopleIds } from '../../utils/mention';
import {
  GET_NOTES,
  CREATE_NOTE,
  UPDATE_NOTE,
  DELETE_NOTE,
  NoteData,
  NoteFormData
} from './queries';

const Notes: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { data, loading, error } = useQuery(GET_NOTES);
  const { data: peopleData, loading: peopleLoading, error: peopleError } = useQuery(GET_PEOPLE);

  if (error) return <Alert severity="error">Failed to load notes: {error.message}</Alert>;

  const peopleList = peopleData?.getPeople || [];

  const [createNote] = useMutation(CREATE_NOTE, {
    refetchQueries: [{ query: GET_NOTES }],
  });

  const [updateNote] = useMutation(UPDATE_NOTE);

  const [deleteNote] = useMutation(DELETE_NOTE, {
    update(cache, { data }, { variables }) {
      const existing: any = cache.readQuery({ query: GET_NOTES });
      if (existing) {
        cache.writeQuery({
          query: GET_NOTES,
          data: { getNotes: existing.getNotes.filter((n: any) => n.id !== variables?.id) },
        });
      }
    }
  });

  const methods = useForm<NoteFormData>({
    defaultValues: { title: '', content: '' }
  });

  const { reset, watch } = methods;
  const formValues = watch();

  const selectedNote = data?.getNotes?.find((n: NoteData) => n.id === selectedNoteId);
  const lastNoteIdRef = React.useRef<string | null>(null);

  const saveNote = useCallback(async (formData: NoteFormData) => {
    if (!selectedNoteId) return;

    const matchedPeopleIds = getLinkedPeopleIds(formData.content, peopleList);

    await updateNote({
      variables: {
        id: selectedNoteId,
        note: {
          title: formData.title,
          content: formData.content,
          isPinned: selectedNote?.isPinned,
          people: matchedPeopleIds
        }
      }
    });
  }, [selectedNoteId, selectedNote?.isPinned, updateNote, peopleList]);

  const { status, setManualData } = useAutosave(formValues, saveNote, 3000);

  useEffect(() => {
    if (selectedNote && selectedNoteId !== lastNoteIdRef.current) {
      lastNoteIdRef.current = selectedNoteId;
      const initialData = {
        title: selectedNote.title,
        content: selectedNote.content,
      };
      reset(initialData);
      setManualData(initialData);
    }
  }, [selectedNoteId, selectedNote, reset, setManualData]);

  const handleAddNote = () => {
    const tempId = `temp_${Date.now()}`;
    setSelectedNoteId(tempId);
    createNote({
      variables: {
        note: { title: 'Untitled Note', content: '', isPinned: false, people: [] }
      }
    }).then(({ data: newData }) => {
      if (newData?.createNote) {
        setSelectedNoteId((prev) => prev === tempId ? newData.createNote.id : prev);
      }
    }).catch((err) => {
      console.error('Failed to create note:', err);
    });
  };

  const handleDeleteNote = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTargetId(id);
  };

  const handleConfirmDeleteNote = async () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    try {
      await deleteNote({ variables: { id: deleteTargetId } });
      if (selectedNoteId === deleteTargetId) setSelectedNoteId(null);
      setDeleteTargetId(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleTogglePin = async (note: NoteData, e: React.MouseEvent) => {
    e.stopPropagation();
    const matchedPeopleIds = getLinkedPeopleIds(note.content, peopleList);
    await updateNote({
      variables: {
        id: note.id,
        note: {
          title: note.title,
          content: note.content,
          isPinned: !note.isPinned,
          people: matchedPeopleIds
        }
      },
      refetchQueries: [{ query: GET_NOTES }]
    });
  };

  if (loading) return <CircularProgress />;

  return (
    <Layout>
      <Box sx={{
        display: 'flex',
        flexGrow: 1,
        minHeight: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper'
      }}>
        {peopleLoading ? null : peopleError ? (
          <Alert severity="warning" sx={{ m: 2 }}>People unavailable: {peopleError.message}</Alert>
        ) : !isMobile ? (
          <>
            <NoteSidebar
              notes={data?.getNotes || []}
              selectedNoteId={selectedNoteId}
              onSelectNote={setSelectedNoteId}
              onAddNote={handleAddNote}
              onDeleteNote={handleDeleteNote}
              onTogglePin={handleTogglePin}
            />

            {selectedNote ? (
              <NoteEditor
                selectedNote={selectedNote}
                methods={methods}
                saveStatus={status}
                people={peopleList}
              />
            ) : (
              <NoteEmptyState />
            )}
          </>
        ) : (
          selectedNoteId === null || !selectedNote ? (
            <NoteSidebar
              notes={data?.getNotes || []}
              selectedNoteId={selectedNoteId}
              onSelectNote={setSelectedNoteId}
              onAddNote={handleAddNote}
              onDeleteNote={handleDeleteNote}
              onTogglePin={handleTogglePin}
            />
          ) : (
            <NoteEditor
              selectedNote={selectedNote}
              methods={methods}
              saveStatus={status}
              onBack={() => setSelectedNoteId(null)}
              people={peopleList}
            />
          )
        )}
      </Box>

      <DeleteNoteDialog
        open={!!deleteTargetId}
        loading={deleteLoading}
        onConfirm={handleConfirmDeleteNote}
        onCancel={() => setDeleteTargetId(null)}
      />
    </Layout>
  );
};

export default Notes;
